/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: ButtonConfigModal.tsx
 ??? Created: 2025-01-28
 ?? Description:
    Modal for editing button configurations inline in PBX CLI page.

 ?? Last Updated: 2025-11-07 by GPT-5 Codex
----------------------------------------------------------------*/

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';

export interface ButtonConfig {
  id?: number;
  uuid?: string;
  label: string;
  tooltip: string | null;
  command: string;
  orderIndex: number;
  enabled: boolean;
}

interface ButtonConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  slotIndex: number;
  existingConfig?: ButtonConfig | null;
}

const shouldValidateFsCli = (command: string) => command.trim().toLowerCase().startsWith('fs_cli');
const isValidFsCliCommand = (command: string) => /^fs_cli\s+-x\s+"[^"]+"$/i.test(command.trim());

export default function ButtonConfigModal({
  isOpen,
  onClose,
  onSave,
  slotIndex,
  existingConfig,
}: ButtonConfigModalProps) {
  const { t } = useI18n();
  const { showError, showSuccess } = useNotifications();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<ButtonConfig>({
    label: '',
    tooltip: '',
    command: '',
    orderIndex: slotIndex,
    enabled: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;

    if (existingConfig) {
      setFormData({
        label: existingConfig.label || '',
        tooltip: existingConfig.tooltip || '',
        command: existingConfig.command || '',
        orderIndex: existingConfig.orderIndex ?? slotIndex,
        enabled: existingConfig.enabled ?? true,
      });
    } else {
      setFormData({
        label: '',
        tooltip: '',
        command: '',
        orderIndex: slotIndex,
        enabled: true,
      });
    }
    setErrors({});
  }, [existingConfig, isOpen, slotIndex]);

  const handleFieldChange = (field: keyof ButtonConfig, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  };

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};

    if (!formData.label.trim()) {
      nextErrors.label = t('pbxCli.buttonConfigs.validation.labelRequired');
    }
    if (!formData.command.trim()) {
      nextErrors.command = t('pbxCli.buttonConfigs.validation.commandRequired');
    } else if (shouldValidateFsCli(formData.command) && !isValidFsCliCommand(formData.command)) {
      nextErrors.command = t('pbxCli.buttonConfigs.validation.invalidFsCli');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('pbxCli.buttonConfigs.messages.validationFailed'));
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        label: formData.label.trim(),
        tooltip: formData.tooltip?.trim() || null,
        command: formData.command.trim(),
        orderIndex: slotIndex,
        enabled: formData.enabled,
      };

      const url = existingConfig?.id
        ? buildApiUrl(`/api/telephony/pbx-cli/button-configs/${existingConfig.id}`)
        : buildApiUrl('/api/telephony/pbx-cli/button-configs');

      const response = await fetch(url, {
        method: existingConfig?.id ? 'PUT' : 'POST',
        headers: {
          ...getApiHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || 'Failed to save button configuration');
      }

      showSuccess(
        existingConfig?.id
          ? t('pbxCli.buttonConfigs.messages.updateSuccess')
          : t('pbxCli.buttonConfigs.messages.createSuccess'),
      );
      onSave();
      onClose();
    } catch (error) {
      showError(error.message || t('pbxCli.buttonConfigs.messages.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingConfig?.id) return;

    setIsSaving(true);
    try {
      const response = await fetch(buildApiUrl(`/api/telephony/pbx-cli/button-configs/${existingConfig.id}`), {
        method: 'DELETE',
        headers: getApiHeaders(),
      });
      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || 'Failed to delete button configuration');
      }

      showSuccess(t('pbxCli.buttonConfigs.messages.deleteSuccess'));
      onSave();
      onClose();
    } catch (error) {
      showError(error.message || t('pbxCli.buttonConfigs.messages.deleteError'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-lg border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="text-lg font-semibold">
            {existingConfig
              ? t('pbxCli.buttonConfigs.modal.editTitle', { slot: slotIndex + 1 })
              : t('pbxCli.buttonConfigs.modal.createTitle', { slot: slotIndex + 1 })}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
            aria-label={t('common:close')}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div>
            <Label htmlFor="modal-label">
              {t('pbxCli.buttonConfigs.fields.label')}
              <span className="ml-1 text-red-500">*</span>
            </Label>
            <Input
              id="modal-label"
              value={formData.label}
              onChange={(event) => handleFieldChange('label', event.target.value)}
              placeholder={t('pbxCli.buttonConfigs.placeholders.label')}
              className={errors.label ? 'border-red-500' : ''}
            />
            {errors.label && <p className="mt-1 text-sm text-red-500">{errors.label}</p>}
          </div>

          <div>
            <Label htmlFor="modal-tooltip">{t('pbxCli.buttonConfigs.fields.tooltip')}</Label>
            <Textarea
              id="modal-tooltip"
              value={formData.tooltip || ''}
              onChange={(event) => handleFieldChange('tooltip', event.target.value)}
              placeholder={t('pbxCli.buttonConfigs.placeholders.tooltip')}
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="modal-command">
              {t('pbxCli.buttonConfigs.fields.command')}
              <span className="ml-1 text-red-500">*</span>
              <SimpleTooltip content={t('pbxCli.buttonConfigs.help.commandFormat')} position="top">
                <span className="ml-1 text-xs text-muted-foreground">(?)</span>
              </SimpleTooltip>
            </Label>
            <Input
              id="modal-command"
              value={formData.command}
              onChange={(event) => handleFieldChange('command', event.target.value)}
              placeholder='fs_cli -x "status"'
              className={`font-mono ${errors.command ? 'border-red-500' : ''}`}
            />
            {errors.command && <p className="mt-1 text-sm text-red-500">{errors.command}</p>}
            <p className="mt-1 text-xs text-muted-foreground">{t('pbxCli.buttonConfigs.help.commandHint')}</p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="modal-enabled">{t('pbxCli.buttonConfigs.fields.enabled')}</Label>
              <p className="text-xs text-muted-foreground">{t('pbxCli.buttonConfigs.help.enabledHint')}</p>
            </div>
            <Toggle
              id="modal-enabled"
              value={formData.enabled}
              onChange={(checked) => handleFieldChange('enabled', checked)}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border p-4">
          <div>
            {existingConfig?.id && (
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={isSaving}
                className="text-red-600 hover:text-red-700"
              >
                {t('common:delete')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              {t('common:cancel')}
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? t('common:saving') : t('common:save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

